<script lang="ts">
  import type { Snippet } from "svelte"

  type ButtonType = "button" | "submit" | "reset"
  type Size = "md" | "sm"
  type Tone = "default" | "subtle" | "danger"

  let {
    type = "button",
    disabled = false,
    size = "md",
    tone = "default",
    class: className = "",
    children,
    ...rest
  }: {
    type?: ButtonType
    disabled?: boolean
    size?: Size
    tone?: Tone
    class?: string
    children?: Snippet
  } & Record<string, unknown> = $props()
</script>

<button
  {type}
  {disabled}
  class={`${["ghost-button", size, tone, className].filter(Boolean).join(" ")}`}
  {...rest}
>
  {@render children?.()}
</button>

<style>
  .ghost-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.55rem;
    border-radius: 999px;
    border: 1px solid color-mix(in srgb, var(--text-100) 12%, transparent);
    background: color-mix(in srgb, var(--bg-200) 85%, var(--bg-100) 15%);
    color: var(--text-100);
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    transition:
      background 0.2s ease,
      transform 0.2s ease,
      box-shadow 0.2s ease,
      border-color 0.2s ease;
    padding: 0.65rem 1.3rem;
    font-size: 0.85rem;
  }

  .ghost-button.sm {
    padding: 0.45rem 1rem;
    font-size: 0.75rem;
    gap: 0.45rem;
  }

  .ghost-button.subtle {
    background: transparent;
    border-color: color-mix(in srgb, var(--text-100) 8%, transparent);
  }

  .ghost-button.danger {
    border-color: rgba(255, 106, 99, 0.45);
    color: #ff6a63;
    background: rgba(173, 81, 76, 0.14);
  }

  .ghost-button:hover:not(:disabled) {
    background: var(--bg-300);
    transform: translateY(-2px);
    box-shadow: 0 12px 26px rgba(10, 8, 8, 0.28);
  }

  .ghost-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .ghost-button:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-200) 25%, transparent);
  }
</style>
